<template>
  <OnBoardingFrame
    as-form
    back
    :title="$t('shared.log_in')"
    :error="errorMessage"
    :notice="noticeMessage"
    @back="backTapped"
    @submit="login"
  >
    <!-- After a reset email is sent, offer quick links into the user's mailbox (spam-inclusive
         where the provider supports it). -->
    <OpenMailboxButtons v-if="noticeMessage" class="ob-mail" />

    <div class="ob-fields">
      <div class="ob-field">
        <label class="ob-label" for="loginEmail">{{ $t('shared.email') }}</label>
        <input
          id="loginEmail"
          v-model.trim="email"
          type="email"
          class="ob-input"
          placeholder="example@email.com"
          autocomplete="email"
          required
        >
      </div>

      <div class="ob-field">
        <label class="ob-label" for="loginPassword">{{ $t('on_boarding.email_login_page.password') }}</label>
        <div class="ob-password">
          <input
            id="loginPassword"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            class="ob-input"
            :placeholder="$t('on_boarding.email_login_page.your_password')"
            autocomplete="current-password"
            required
          >
          <button
            type="button"
            class="ob-icon-btn"
            :aria-label="showPassword ? $t('on_boarding.email_login_page.hide_password') : $t('on_boarding.email_login_page.show_password')"
            @click="showPassword = !showPassword"
          >
            <span class="ms fill" aria-hidden="true">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
          </button>
        </div>
      </div>
    </div>

    <button type="button" class="ob-link" @click="resetPassword">
      {{ $t('on_boarding.email_login_page.forgot_your_password_send_reset_email') }}
    </button>

    <template #footer>
      <button type="submit" class="ob-btn ob-btn-primary" :disabled="isLoading || !email || !password">
        <template v-if="isLoading">
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span>{{ $t('on_boarding.email_login_page.logging_in') }}</span>
        </template>
        <template v-else>{{ $t('shared.log_in') }}</template>
      </button>
    </template>
  </OnBoardingFrame>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { isValidEmail } from '../../../utils/helpers/IsValidEmail';
import OpenMailboxButtons from '../../../utils/shared_components/OpenMailboxButtons.vue';
import OnBoardingFrame from './OnBoardingFrame.vue';

export default defineComponent({
  name: 'OnBoardingEmailLoginPage',
  components: { OnBoardingFrame, OpenMailboxButtons },
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
    };
  },
  computed: {
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    noticeMessage(): string | null {
      return this.$store.state.onBoarding.noticeMessage;
    },
  },
  methods: {
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/setNotice', null);
      this.$store.commit('onBoarding/popPage');
    },
    async login() {
      // Clear any password-reset notice from a previous tap so it doesn't
      // sit alongside a fresh login attempt.
      this.$store.commit('onBoarding/setNotice', null);
      await this.$store.dispatch('onBoarding/loginWithEmail', {
        email: this.email,
        password: this.password,
      });
    },
    resetPassword() {
      if (this.email && isValidEmail(this.email)) {
        this.$store.dispatch('onBoarding/resetPassword', this.email);
      } else {
        this.$store.commit('onBoarding/setError', this.$t('on_boarding.email_login_page.enter_valid_email_then_reset'));
      }
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
    this.$store.commit('onBoarding/setNotice', null);
  },
});
</script>
